import { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

// ScriptRecorder — handoff v3 rewrite
// ──────────────────────────────────────────────────────────────────────
// Same two-column layout as before (camera stage left, side panel right),
// but the surface is far richer:
//
//   permission — pre-flight card explaining why we need cam+mic, with
//                a single big "Allow camera & microphone" button. Shows
//                an error helper if the browser denies access. Replaces
//                the old "tap to turn on camera" intro.
//
//   setup      — camera live, side panel shows the start pill ("Tap to
//                start recording / 5-second countdown, then read aloud"),
//                three setup tips (lighting / quiet room / re-record),
//                and optionally a network-slow warning. Real-time audio
//                meter bars below the stage so the customer sees the mic
//                is working before they record.
//
//   countdown  — 5 second prep (was 3) with per-second tips in the
//                stage overlay ("Take a breath" → "Sit up straight" →
//                "Look at the camera" → "Smile if you feel like it" →
//                "Here we go").
//
//   recording  — MediaRecorder capturing; audio bars continue;
//                encouragement messages fade in at 6s/14s/22s ("Doing
//                great. Just keep reading.", etc); silence detection
//                auto-stops the take after 2.5s of quiet (only after
//                5s of recording elapsed, so a thinking pause doesn't
//                trigger it); 3-2-1 toast as we approach the auto-stop.
//
//   uploading  — Stream's two-step model surfaced as two visible stages:
//                  1. Sending      (real % progress from XHR onprogress)
//                  2. Processing   (real /accept call, ends when it
//                                   resolves and the row is in DB)
//                Then 'ready' (visible briefly) → phase flips to review
//                automatically. Wait-tip below the bar.
//
//   review     — preview the take. Friendly "How did that feel?" prompt
//                above the action row. Redo / Keep this one buttons.
//
//   error      — friendly error message + retry CTA.
//
// Live preview is mirrored (selfie-style); recorded blob is NOT mirrored
// so the marketing-ready video has correct orientation. Audio bars use
// a real AnalyserNode hooked to the mic stream — they reflect actual
// volume so the customer can see "yes, you can hear me" before they
// record (a real reassurance step for boomers).
//
// All upstream interactions are unchanged from the prior version:
//   POST /api/clip/upload-url    → { clip_id, upload_url }  (Stream direct-upload)
//   XHR multipart POST upload_url → puts the webm to Stream
//   POST /api/clip/:id/accept    → finalizes, returns the clip row
//
// State machine:
//   permission → setup → countdown → recording → uploading → review
//                                    ↑                       ↓
//                                    └──────── redo ─────────┘
//   any → error → permission (retry)

const PREFERRED_MIMES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4;codecs=h264,aac',
  'video/mp4',
];

function pickMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of PREFERRED_MIMES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

const TELE_SIZES = { sm: 22, md: 30, lg: 38 };
const TELE_PREF_KEY = 'vt-tele-size';

// Silence detection thresholds. RMS < 0.04 for HOLD_MS triggers the
// auto-stop, but only if at least MIN_RECORDED_MS has elapsed (so a
// thoughtful pause at the start doesn't kill the take). The 3-2-1
// warning toast surfaces during the last 3 seconds of the hold so the
// customer has a chance to recover.
const SILENCE_RMS_THRESHOLD = 0.04;
const SILENCE_HOLD_MS       = 2500;
const SILENCE_MIN_ELAPSED   = 5;     // seconds of recording before auto-stop is allowed

// Encouragement messages — keyed by elapsed seconds during recording.
// Picked to feel like a friend nodding from off-camera, not a
// drill-sergeant. Each shows for 2.8s then fades.
const ENCOURAGEMENT_BY_SECOND = {
  6:  'Doing great. Just keep reading.',
  14: 'You sound natural. Nice work.',
  22: 'Almost there. Stay with it.',
};

// Per-second tip text during the 5-second countdown. count is 5..1, then
// 'Go!' at 0. Boomer-friendly: each tells them one tiny thing to do.
const COUNTDOWN_TIPS = {
  5: 'Take a breath.',
  4: 'Sit up straight.',
  3: 'Look at the camera.',
  2: 'Smile if you feel like it.',
  1: 'Here we go.',
};

export default function ScriptRecorder({
  script,
  invite_token,
  maxDurationSeconds = 60,
  filledScript,
  onAccepted,
  onCancel,
  clipNumber = 1,
  totalClips = 3,
}) {
  // ── Phase + permission state ────────────────────────────────────────
  // Phase machine — see top-of-file diagram for state transitions.
  const [phase, setPhase] = useState('permission');
  const [permState, setPermState] = useState('idle'); // idle | asking | denied
  const [error, setError] = useState(null);

  // ── Recording state ────────────────────────────────────────────────
  const [countdown, setCountdown] = useState(5);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [takeNumber, setTakeNumber] = useState(1);
  const [previewUrl, setPreviewUrl] = useState(null);

  // ── Upload state (two visible stages: sending vs processing) ──────
  // sending  — XHR PUT in flight, uploadPct is real progress.
  // processing — XHR completed, /accept call in flight.
  // ready    — /accept returned, brief 'all done' beat before flipping
  //            to review phase.
  const [uploadStage, setUploadStage] = useState('sending');
  const [uploadPct, setUploadPct] = useState(0);

  // ── Audio meter + silence detection ───────────────────────────────
  // bars are 32 floats 0..1 driven by real FFT data. silentMs counts
  // up while RMS is below threshold during recording; resets when we
  // hear them again. autoStopIn is null normally; counts down 3/2/1
  // as we approach the silence cutoff so the customer sees it coming.
  const [bars, setBars] = useState(() => new Array(32).fill(0.1));
  const [autoStopIn, setAutoStopIn] = useState(null);

  // ── Ambient state ─────────────────────────────────────────────────
  const [encouragement, setEncouragement] = useState(null);
  const [networkSlow, setNetworkSlow] = useState(false);
  const [teleSize, setTeleSize] = useState(() => {
    try { return localStorage.getItem(TELE_PREF_KEY) || 'lg'; } catch { return 'lg'; }
  });

  // ── Refs ──────────────────────────────────────────────────────────
  const liveVideoRef    = useRef(null);
  const reviewVideoRef  = useRef(null);
  const mediaStreamRef  = useRef(null);
  const recorderRef     = useRef(null);
  const chunksRef       = useRef([]);
  const blobRef         = useRef(null);
  const recordTimerRef  = useRef(null);
  const startedAtRef    = useRef(null);

  // Audio analysis refs — one AudioContext + AnalyserNode lives for the
  // whole component once we have the stream. The rAF loop reads from
  // this analyser and updates bars + silence counters.
  const audioCtxRef     = useRef(null);
  const analyserRef     = useRef(null);
  const sourceNodeRef   = useRef(null);
  const rafRef          = useRef(null);
  const silenceMsRef    = useRef(0);     // mutable counter, avoids re-renders on every rAF tick

  useEffect(() => {
    try { localStorage.setItem(TELE_PREF_KEY, teleSize); } catch { /* ignore */ }
  }, [teleSize]);

  // ── Cleanup on unmount ────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { audioCtxRef.current?.close(); } catch { /* ignore */ }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Permission preflight ──────────────────────────────────────────
  // Async getUserMedia with a friendly fallback constraint set. On
  // success: hold the stream in mediaStreamRef and advance to 'setup'.
  // Also: probe navigator.connection.effectiveType for slow-network
  // warning copy on the setup screen.
  const requestPermission = useCallback(async () => {
    setError(null);
    setPermState('asking');

    // Detect slow connections — shown as a warning during setup.
    // The API isn't universal (Firefox+Safari miss it) but where it
    // exists it's useful.
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const et = conn?.effectiveType;
    const slow = et === '2g' || et === 'slow-2g' || (conn?.downlink && conn.downlink < 1);
    setNetworkSlow(!!slow);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('This browser cannot use the camera. Please open the link in Safari, Chrome, or Edge.');
      setPermState('denied');
      return;
    }

    const tryGet = (constraints) => navigator.mediaDevices.getUserMedia(constraints);
    let stream;
    try {
      stream = await tryGet({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
    } catch (e1) {
      console.warn('getUserMedia first attempt failed', e1?.name, e1?.message);
      const retryable = ['OverconstrainedError', 'ConstraintNotSatisfiedError', 'NotReadableError', 'AbortError'];
      if (retryable.includes(e1?.name)) {
        try { stream = await tryGet({ video: true, audio: true }); }
        catch (e2) { return failWith(e2); }
      } else {
        return failWith(e1);
      }
    }

    mediaStreamRef.current = stream;

    // Wire up the analyser node on the same stream — drives the bars
    // and the silence counter. AudioContext sometimes needs a user
    // gesture to start; this function IS a user gesture (clicked the
    // Allow button) so the resume() call below is safe.
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        if (ctx.state === 'suspended') await ctx.resume();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;          // 128 freq bins; downsampled to 32 bars
        analyser.smoothingTimeConstant = 0.6;
        source.connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        sourceNodeRef.current = source;
      }
    } catch (e) {
      // Audio meter is a nice-to-have; if it fails, recording still works.
      console.warn('AudioContext setup failed', e?.name, e?.message);
    }

    setPermState('idle');
    setPhase('setup');

    function failWith(e) {
      console.error('getUserMedia failed', e?.name, e?.message);
      const name = e?.name || 'Error';
      const friendly = {
        NotAllowedError:               'Camera access was blocked. Open your browser settings, allow camera and microphone for this page, then tap Try again.',
        PermissionDeniedError:         'Camera access was blocked. Open your browser settings, allow camera and microphone for this page, then tap Try again.',
        NotFoundError:                 'No camera was found on this device.',
        DevicesNotFoundError:          'No camera was found on this device.',
        NotReadableError:              'Your camera is already being used by another app. Close it (FaceTime, Zoom, etc.) and tap Try again.',
        TrackStartError:               'Your camera is already being used by another app. Close it and tap Try again.',
        OverconstrainedError:          'Your camera does not support recording in this browser.',
        ConstraintNotSatisfiedError:   'Your camera does not support recording in this browser.',
        SecurityError:                 'This page must be loaded over a secure (https) connection to use the camera.',
        AbortError:                    'Camera startup was interrupted. Tap Try again.',
      }[name] || `We could not access your camera. (${name})`;
      setError(friendly);
      setPermState('denied');
    }
  }, []);

  // ── Attach live stream to <video> for phases that show preview ────
  useEffect(() => {
    const v = liveVideoRef.current;
    const stream = mediaStreamRef.current;
    const phaseShowsLive = phase === 'setup' || phase === 'countdown' || phase === 'recording' || phase === 'uploading';
    if (!v || !stream || !phaseShowsLive) return;
    if (v.srcObject === stream) return;

    v.srcObject = stream;
    v.muted = true;           // no feedback loop on the preview
    v.playsInline = true;
    const playPromise = v.play();
    if (playPromise && playPromise.catch) {
      playPromise.catch((err) => console.warn('video.play() rejected', err?.name, err?.message));
    }
  }, [phase]);

  // ── Audio meter rAF loop ──────────────────────────────────────────
  // Active during setup + recording. Reads real FFT data, downsamples
  // to 32 bars, computes peak as a proxy for "are they making sound."
  // Silence detection runs on top of the same loop during recording.
  useEffect(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const meterActive = phase === 'setup' || phase === 'recording';
    if (!meterActive) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const buf = new Uint8Array(analyser.frequencyBinCount);
    let last = performance.now();
    silenceMsRef.current = 0;

    const tick = () => {
      const now = performance.now();
      const dt = now - last;
      last = now;

      analyser.getByteFrequencyData(buf);

      // Downsample 128 bins to 32 bars by averaging chunks of 4.
      // Also compute peak across all bins, used for silence detection.
      const next = new Array(32);
      let peak = 0;
      const step = buf.length / 32;
      for (let i = 0; i < 32; i++) {
        let sum = 0;
        const start = Math.floor(i * step);
        const end   = Math.floor((i + 1) * step);
        for (let j = start; j < end; j++) sum += buf[j];
        const avg = sum / (end - start) / 255;   // → 0..1
        next[i] = avg;
        if (avg > peak) peak = avg;
      }
      setBars(next);

      // Silence detection — only counts during the recording phase.
      if (phase === 'recording') {
        if (peak < SILENCE_RMS_THRESHOLD) {
          silenceMsRef.current += dt;
          // Show the 3-2-1 warning when we're inside the last 3000ms of the hold
          if (silenceMsRef.current > SILENCE_HOLD_MS - 3000) {
            const remaining = Math.max(0, Math.ceil((SILENCE_HOLD_MS - silenceMsRef.current) / 1000));
            setAutoStopIn(remaining || 1);
          }
          const recordedSeconds = startedAtRef.current ? (Date.now() - startedAtRef.current) / 1000 : 0;
          if (silenceMsRef.current >= SILENCE_HOLD_MS && recordedSeconds > SILENCE_MIN_ELAPSED) {
            stopRecording();
            return;   // exit early — the next tick will be cancelled when phase changes
          }
        } else {
          if (silenceMsRef.current > 0) silenceMsRef.current = 0;
          setAutoStopIn(null);
        }
      } else {
        silenceMsRef.current = 0;
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── Encouragement messages during recording ───────────────────────
  // Surfaces at ENCOURAGEMENT_BY_SECOND keys. Each shows for 2.8s.
  useEffect(() => {
    if (phase !== 'recording') { setEncouragement(null); return; }
    const msg = ENCOURAGEMENT_BY_SECOND[recordSeconds];
    if (msg) {
      setEncouragement(msg);
      const id = setTimeout(() => setEncouragement(null), 2800);
      return () => clearTimeout(id);
    }
  }, [phase, recordSeconds]);

  // ── 5-second countdown ───────────────────────────────────────────
  const startCountdown = useCallback(() => {
    setPhase('countdown');
    setCountdown(5);
    const id = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(id);
          startRecording();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Begin recording ──────────────────────────────────────────────
  const startRecording = useCallback(() => {
    const stream = mediaStreamRef.current;
    if (!stream) { setError('Camera not ready.'); setPhase('error'); return; }

    const mime = pickMime();
    let recorder;
    try {
      recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
    } catch (e) {
      setError('Your browser does not support video recording. Please try Chrome, Edge, or Safari on a newer device.');
      setPhase('error');
      return;
    }
    recorderRef.current = recorder;
    chunksRef.current = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime || 'video/webm' });
      blobRef.current = blob;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPhase('review');
    };
    recorder.start(250);
    startedAtRef.current = Date.now();
    setRecordSeconds(0);
    setPhase('recording');

    recordTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startedAtRef.current) / 1000;
      setRecordSeconds(Math.floor(elapsed));
      if (elapsed >= maxDurationSeconds) stopRecording();
    }, 200);
  }, [maxDurationSeconds, previewUrl]);

  const stopRecording = useCallback(() => {
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    setAutoStopIn(null);
  }, []);

  // ── Redo: discard the blob, return to setup phase ─────────────────
  const redo = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    blobRef.current = null;
    setTakeNumber((n) => n + 1);
    setRecordSeconds(0);
    setPhase('setup');
  }, [previewUrl]);

  // ── Accept the take: upload to Stream, then /accept ───────────────
  // Two visible stages:
  //   1. sending      — real % from XHR onprogress
  //   2. processing   — /accept call in flight (no useful progress info,
  //                     so we just show "Just a moment…")
  //   3. ready        — brief beat before phase flips to review
  const acceptTake = useCallback(async () => {
    if (!blobRef.current) return;
    setPhase('uploading');
    setUploadPct(0);
    setUploadStage('sending');
    setError(null);

    try {
      const { clip_id, upload_url } = await api('/api/clip/upload-url', {
        method: 'POST',
        body: {
          invite_token,
          script_key: script.key,
          filled_script: filledScript,
          max_duration_seconds: maxDurationSeconds + 5,
        },
      });

      const fd = new FormData();
      fd.append('file', blobRef.current, `clip_${script.key}.webm`);

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', upload_url, true);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadPct(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`upload ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('upload network error'));
        xhr.send(fd);
      });

      // Upload complete — XHR finished. Flip to 'processing' while /accept runs.
      setUploadPct(100);
      setUploadStage('processing');

      const duration = blobRef.current.size ? recordSeconds || null : null;
      const clip = await api(`/api/clip/${clip_id}/accept`, {
        method: 'POST',
        body: { invite_token, filled_script: filledScript, duration_seconds: duration },
      });

      // /accept resolved. Brief "Ready!" beat, then leave the screen.
      setUploadStage('ready');
      setTimeout(() => onAccepted?.({ ...clip, clip_id }), 700);
    } catch (e) {
      console.error('upload/accept failed', e);
      setError("We couldn't save that take. Check your internet connection and try again.");
      setPhase('review');
    }
  }, [invite_token, script.key, filledScript, maxDurationSeconds, recordSeconds, onAccepted]);

  // ── Derived display state ────────────────────────────────────────
  const mm = String(Math.floor(recordSeconds / 60)).padStart(2, '0');
  const ss = String(recordSeconds % 60).padStart(2, '0');
  const maxMm = String(Math.floor(maxDurationSeconds / 60)).padStart(2, '0');
  const maxSs = String(maxDurationSeconds % 60).padStart(2, '0');

  const canLeave = phase !== 'recording' && phase !== 'uploading' && phase !== 'countdown';

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="vt-rec-page">
      <header className="vt-rec-top">
        <div className="vt-rec-left">
          <img
            className="vt-rec-logo"
            src="https://63b4b496a6f4b9b3afd358772c056add.cdn.bubble.io/f1753191012861x650931340984918900/Auto%20Layout%20Horizontal.svg"
            alt="Voiply"
          />
        </div>
        <div className="vt-rec-title">
          <span className="vt-rec-clip-of">Clip {clipNumber} of {totalClips}</span>
          {script?.title || 'Recording'}
        </div>
        <div className="vt-rec-right">
          <button
            type="button"
            className="vt-rec-close"
            onClick={onCancel}
            disabled={!canLeave}
            aria-label="Back to script"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
            Back
          </button>
        </div>
      </header>

      {/* ── Permission preflight ─────────────────────────────────── */}
      {phase === 'permission' && (
        <div className="vt-rec-preflight">
          <div className="vt-preflight-card animate-fade-in-up">
            <div className="vt-preflight-icons" aria-hidden="true">
              <span className="vt-pf-icon vt-pf-cam">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="6" width="13" height="12" rx="2"/>
                  <path d="M16 10l5-3v10l-5-3z"/>
                </svg>
              </span>
              <span className="vt-pf-plus">+</span>
              <span className="vt-pf-icon vt-pf-mic">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="3" width="6" height="12" rx="3"/>
                  <path d="M5 11a7 7 0 0 0 14 0"/>
                  <line x1="12" y1="18" x2="12" y2="22"/>
                </svg>
              </span>
            </div>
            <h2 className="vt-preflight-title">Let's get your camera ready.</h2>
            <p className="vt-preflight-sub">
              We need your camera and microphone to record. Your browser will ask first.
              Tap <strong>Allow</strong> when it does.
            </p>

            {/* Boomer-friendly recording tips. Added after we got a
                recording where the customer read directly off the
                screen — her bifocals reflected the teleprompter text
                back at the camera. The tips here target the three
                things that go wrong most often with this demographic:
                1) reading instead of looking at the camera (which the
                   reflection bug is a symptom of),
                2) backlit window framing (silhouette / harsh light),
                3) trying to be too perfect (over-rehearsed feel).
                The glasses tip is its own line because it's specific
                and not obvious to non-glasses-wearers reading copy.
                Tone is reassuring + concrete — older customers respond
                better to "do this" than to "consider whether..." */}
            <div className="vt-preflight-tips">
              <div className="vt-preflight-tips-head">
                <span className="vt-pf-tips-pill">3 quick tips</span>
                <span className="vt-pf-tips-sub">It takes 30 seconds to set up. Worth it!</span>
              </div>
              <ul className="vt-preflight-tips-list">
                <li>
                  <span className="vt-pf-tip-ico" aria-hidden="true">👀</span>
                  <div>
                    <strong>Look at the camera, not the words.</strong>
                    <span>Read a line, then look up and say it like you're talking to a friend. You don't have to get every word exactly right.</span>
                  </div>
                </li>
                <li>
                  <span className="vt-pf-tip-ico" aria-hidden="true">💡</span>
                  <div>
                    <strong>Face a window or a lamp.</strong>
                    <span>The light should be in front of you. If a window's behind you, turn around so it's facing you instead.</span>
                  </div>
                </li>
                <li>
                  <span className="vt-pf-tip-ico" aria-hidden="true">👓</span>
                  <div>
                    <strong>If you wear glasses, tilt the screen down a bit.</strong>
                    <span>That way the screen doesn't reflect in your lenses.</span>
                  </div>
                </li>
              </ul>
            </div>

            {(permState === 'denied' || error) && (
              <div className="vt-preflight-error" role="alert">
                {error || 'Your browser blocked the camera. Check your browser settings and try again.'}
              </div>
            )}
            <button
              className="vt-btn vt-btn-primary vt-btn-lg vt-tap vt-preflight-cta"
              onClick={requestPermission}
              disabled={permState === 'asking'}
            >
              {permState === 'asking' ? 'Waiting for you…'
                : permState === 'denied' ? 'Try again'
                : 'Allow camera & microphone →'}
            </button>
            <p className="vt-preflight-tip">
              We only use it for this recording. Nothing is sent until you press Submit.
            </p>
          </div>
        </div>
      )}

      {/* ── Everything else: camera stage + side panel ───────────── */}
      {phase !== 'permission' && (
        <div className="vt-rec-body">
          {/* Camera stage */}
          <div className={'vt-rec-stage vt-phase-' + phase}>
            {/* Live preview — mirrored selfie-style */}
            {(phase === 'setup' || phase === 'countdown' || phase === 'recording' || phase === 'uploading') && (
              <video
                ref={liveVideoRef}
                playsInline muted autoPlay
                style={{ transform: 'scaleX(-1)' }}
              />
            )}

            {/* Playback — not mirrored, this is what the audience sees */}
            {phase === 'review' && previewUrl && (
              <video
                ref={reviewVideoRef}
                playsInline controls
                src={previewUrl}
              />
            )}

            {phase === 'error' && (
              <div className="vt-rec-cam-placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <div className="vt-rec-cam-placeholder-title">Camera unavailable</div>
                <div className="vt-rec-cam-placeholder-body">Tap "Try again" below to grant permission.</div>
              </div>
            )}

            {/* Status pill + timer overlay (top row) */}
            {(phase === 'setup' || phase === 'countdown' || phase === 'recording' || phase === 'uploading' || phase === 'review') && (
              <div className="vt-rec-overlay">
                <div className="vt-rec-top-row">
                  {phase === 'setup' && (
                    <span className="vt-rec-status ready"><span className="vt-rec-status-dot" />Camera ready</span>
                  )}
                  {phase === 'countdown' && (
                    <span className="vt-rec-status review"><span className="vt-rec-status-dot" />Get comfortable</span>
                  )}
                  {phase === 'recording' && (
                    <>
                      <span className="vt-rec-status"><span className="vt-rec-status-dot" />RECORDING</span>
                      <span className="vt-rec-timer">{mm}:{ss} / {maxMm}:{maxSs}</span>
                    </>
                  )}
                  {phase === 'uploading' && (
                    <span className="vt-rec-status review"><span className="vt-rec-status-dot" />Saving your video</span>
                  )}
                  {phase === 'review' && (
                    <span className="vt-rec-status review">
                      <span className="vt-rec-status-dot" />How did it go?
                      {takeNumber > 1 && ` · TAKE ${takeNumber}`}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Countdown overlay — big digit + per-second tip */}
            {phase === 'countdown' && (
              <div className="vt-countdown-overlay">
                <div className="vt-countdown-num" key={countdown}>
                  {countdown > 0 ? countdown : 'Go!'}
                </div>
                <div className="vt-countdown-tip">
                  {COUNTDOWN_TIPS[countdown] || ' '}
                </div>
              </div>
            )}

            {/* In-stage running teleprompter (visible only while recording) */}
            {phase === 'recording' && (
              <div className="vt-rec-tele-overlay">
                {renderRunningTeleprompter(script, filledScript, recordSeconds, maxDurationSeconds)}
              </div>
            )}

            {/* Encouragement toast — fades in/out */}
            {encouragement && (
              <div className="vt-encouragement" aria-live="polite">
                <span className="vt-enc-icon">♥</span>{encouragement}
              </div>
            )}

            {/* Auto-stop warning — appears when we're 3 seconds from cutoff */}
            {phase === 'recording' && autoStopIn !== null && (
              <div className="vt-auto-stop-toast" aria-live="polite">
                <span className="vt-rec-status-dot" />
                Quiet… stopping in {autoStopIn}s
              </div>
            )}

            {/* Audio bars — real mic data, visible during setup + recording */}
            {(phase === 'setup' || phase === 'recording') && (
              <div className="vt-audio-bars" aria-hidden="true">
                {bars.map((h, i) => (
                  <span
                    key={i}
                    className="vt-audio-bar"
                    style={{ height: `${Math.max(6, h * 100)}%` }}
                  />
                ))}
              </div>
            )}

            {/* Two-stage upload overlay */}
            {phase === 'uploading' && (
              <div className="vt-upload-overlay">
                <div className="vt-upload-card">
                  <div className="vt-upload-stages">
                    <div className={'vt-us-row' + (uploadStage !== 'sending' ? ' done' : ' active')}>
                      <span className="vt-us-dot">
                        {uploadStage !== 'sending' ? '✓' : '1'}
                      </span>
                      <div className="vt-us-text">
                        <div className="vt-us-title">Sending your video</div>
                        <div className="vt-us-sub">
                          {uploadStage === 'sending' ? `${uploadPct}% sent` : 'Sent safely'}
                        </div>
                      </div>
                    </div>
                    <div className={'vt-us-row' + (uploadStage === 'ready' ? ' done' : uploadStage === 'processing' ? ' active' : '')}>
                      <span className="vt-us-dot">
                        {uploadStage === 'ready' ? '✓'
                          : uploadStage === 'processing' ? <span className="vt-spinny" />
                          : '2'}
                      </span>
                      <div className="vt-us-text">
                        <div className="vt-us-title">Getting it ready to watch</div>
                        <div className="vt-us-sub">
                          {uploadStage === 'processing' ? 'Just a moment…'
                            : uploadStage === 'ready' ? 'Ready!'
                            : 'Coming up next'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="vt-upload-bar">
                    <span style={{ width: `${uploadStage === 'ready' || uploadStage === 'processing' ? 100 : uploadPct}%` }} />
                  </div>
                  <p className="vt-upload-tip">
                    Hang tight. We're saving this safely. Nothing is shared with anyone yet.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Side panel: teleprompter + phase-specific CTAs */}
          <div className="vt-rec-side">
            <div className="vt-tele-panel">
              <div className="vt-tele-panel-head">
                <h3>
                  {phase === 'setup'     && 'Practice in your head first'}
                  {phase === 'countdown' && 'Get ready to read'}
                  {phase === 'recording' && 'Read this out loud'}
                  {phase === 'uploading' && 'Saving your video'}
                  {phase === 'review'    && "Here's what you said"}
                  {phase === 'error'     && 'Something went wrong'}
                </h3>
                <div className="vt-tele-size-toggle" role="group" aria-label="Teleprompter text size">
                  {['sm', 'md', 'lg'].map((sz) => (
                    <button
                      key={sz}
                      type="button"
                      className={teleSize === sz ? 'active' : ''}
                      onClick={() => setTeleSize(sz)}
                      aria-pressed={teleSize === sz}
                      aria-label={sz === 'sm' ? 'Small text' : sz === 'md' ? 'Medium text' : 'Large text'}
                    >
                      <span className={'vt-s' + (sz === 'sm' ? '1' : sz === 'md' ? '2' : '3')}>A</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tiny contextual reminder during setup. Same message as the
                  preflight tips, but positioned where the customer is
                  literally about to make the choice (read off screen vs
                  look at camera). On setup phase only — once recording
                  starts we get out of the way. */}
              {phase === 'setup' && (
                <div className="vt-tele-cue" role="note">
                  <span className="vt-tele-cue-ico" aria-hidden="true">👀</span>
                  <span>
                    Glance at the words, then <strong>look at the camera</strong> and say it.
                    Don't worry about reading it word-for-word.
                  </span>
                </div>
              )}

              <div className="vt-tele" style={{ '--vt-tele-fs': TELE_SIZES[teleSize] + 'px' }}>
                {renderTeleprompterJSX(script, filledScript)}
              </div>
            </div>

            {/* Phase-specific CTA stack */}
            <div className="vt-rec-cta-stack">
              {phase === 'setup' && (
                <>
                  {networkSlow && (
                    <div className="vt-net-warning" role="alert">
                      <span className="vt-net-warning-ico">📶</span>
                      <div className="vt-net-warning-text">
                        <strong>Your connection looks slow.</strong>
                        <span>Try Wi-Fi if you can. Your video may take a little longer to save.</span>
                      </div>
                    </div>
                  )}
                  <button className="vt-rec-start-pill vt-tap" onClick={startCountdown} aria-label="Start recording">
                    <span className="vt-rec-start-icon"><span className="vt-rec-start-icon-dot" /></span>
                    <span className="vt-rec-start-text">
                      <span className="vt-rec-start-title">Tap to start recording</span>
                      <span className="vt-rec-start-sub">5-second countdown, then read aloud</span>
                    </span>
                  </button>
                  <div className="vt-setup-tips">
                    <span className="vt-setup-tip">
                      <span className="vt-tip-ico" style={{ ['--vt-ico']: '#FB522C' }}>☼</span>
                      <span>Face a window so your face is lit</span>
                    </span>
                    <span className="vt-setup-tip">
                      <span className="vt-tip-ico" style={{ ['--vt-ico']: '#1A7A4D' }}>♪</span>
                      <span>Quiet room. We can hear you well.</span>
                    </span>
                    <span className="vt-setup-tip">
                      <span className="vt-tip-ico" style={{ ['--vt-ico']: '#D43A6F' }}>♥</span>
                      <span>Re-record as many times as you like</span>
                    </span>
                  </div>
                </>
              )}

              {phase === 'countdown' && (
                <>
                  <div className="vt-rec-busy-pill">
                    Camera starts in {countdown > 0 ? countdown : 0}…
                  </div>
                  <div className="vt-rec-hint">You can always stop and start over.</div>
                </>
              )}

              {phase === 'recording' && (
                <>
                  <button className="vt-rec-stop-pill vt-tap" onClick={stopRecording}>
                    <span className="vt-stop-square" />
                    I'm done. Stop recording.
                  </button>
                  <div className="vt-rec-hint">Take your time. You can pause whenever.</div>
                </>
              )}

              {phase === 'uploading' && (
                <>
                  <div className="vt-rec-busy-pill">
                    {uploadStage === 'sending'    ? 'Sending…'
                      : uploadStage === 'processing' ? 'Almost ready…'
                      : 'Ready!'}
                  </div>
                  <div className="vt-rec-hint">Don't close the page yet. This takes a moment.</div>
                </>
              )}

              {phase === 'review' && (
                <>
                  <div className="vt-review-prompt">
                    <span className="vt-review-prompt-ico">🙂</span>
                    <span>How did that feel?</span>
                  </div>
                  <div className="vt-rec-action-row">
                    <button className="vt-btn vt-btn-secondary vt-tap" onClick={redo}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                        <path d="M3 12a9 9 0 1 0 3-6.7" />
                        <polyline points="3 4 3 9 8 9" />
                      </svg>
                      Try again
                    </button>
                    <button className="vt-btn vt-btn-primary vt-btn-lg vt-tap" onClick={acceptTake}>
                      Keep this one →
                    </button>
                  </div>
                  <div className="vt-rec-hint">Nothing is final until you press Submit on the last screen.</div>
                </>
              )}

              {phase === 'error' && (
                <>
                  <div className="vt-rec-error" role="alert">{error}</div>
                  <div className="vt-rec-action-row">
                    <button
                      className="vt-btn vt-btn-primary vt-btn-lg vt-tap"
                      onClick={() => { setError(null); setPhase('permission'); }}
                    >
                      Try again
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Teleprompter rendering — JSX so we can color the filled blanks orange
// to distinguish them from the script's static text at a glance.
// ─────────────────────────────────────────────────────────────────────────

function renderTeleprompterJSX(script, fills) {
  if (!script || !script.prompts) return null;
  const segs = [];
  script.prompts.forEach((p, pi) => {
    if (pi > 0) {
      segs.push(<br key={`br${pi}a`} />);
      segs.push(<br key={`br${pi}b`} />);
    }
    const parts = p.template.split('___');
    parts.forEach((t, i) => {
      if (t) segs.push(<span key={`${pi}t${i}`}>{t}</span>);
      if (i < parts.length - 1) {
        const v = ((fills || {})[`${pi}.${i}`] || '').trim();
        if (v) {
          segs.push(<strong key={`${pi}b${i}`} style={{ color: '#F53900', fontWeight: 500 }}>{v}</strong>);
        } else {
          segs.push(<span key={`${pi}b${i}`} style={{ color: '#9CA3AF' }}>______</span>);
        }
      }
    });
  });
  return <>{segs}</>;
}

// In-stage running teleprompter — surfaces the prompt the speaker should
// be reading right now, derived from elapsed/target ratio.
function renderRunningTeleprompter(script, fills, elapsed, target) {
  if (!script || !script.prompts || script.prompts.length === 0) return null;
  const pct = Math.min(1, elapsed / Math.max(1, target));
  const promptIdx = Math.min(script.prompts.length - 1, Math.floor(pct * script.prompts.length));
  const p = script.prompts[promptIdx];
  if (!p) return null;
  const parts = p.template.split('___');
  return (
    <>
      {parts.map((t, i) => (
        <span key={i}>
          {t}
          {i < parts.length - 1 && (
            <strong style={{ color: '#F53900', fontWeight: 500 }}>
              {((fills || {})[`${promptIdx}.${i}`] || '____').trim() || '____'}
            </strong>
          )}
        </span>
      ))}
    </>
  );
}
